import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  BatteryFull,
  ChevronLeft,
  CircleUser,
  LayoutGrid,
  MessageSquare,
  Pause,
  Play,
  Plus,
  Repeat,
  RotateCcw,
  SendHorizontal,
  Signal,
  VenetianMask,
  Wifi,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/ui/icon";
import { getIdentityColor } from "@/lib/pathformatter";
import { cn } from "@/lib/utils";
import { ribbon, type Pt } from "@/components/sheet/ribbon";
import { loopExamplesFor, type LoopExample } from "../constants";

/*
  aSTRING on a phone: the Loops tab of the app, playing itself.

  The page's argument is that aSTRING lives on the phone, so the first thing
  under the headline is a phone running it. It plays the one gesture a Loop is
  about: the feed, a tap on a room, the room pushes in and people are talking
  in it; back out, the next room. Nothing was joined.

  The screen is painted in the Ink theme, the app's default for a new account
  and the same dark ground as the iOS app, by setting `data-theme="ink"` on it:
  the app's tokens re-resolve there (DESIGN.md §2, "A nested theme
  re-resolves"), so the phone shows the colours you get after signing in.

  The device is a fixed size and every screen is laid out inside it, so the
  demo can push, pop and add messages without the page moving by a pixel; the
  wide demo that sat here before grew and shrank with each room, which on a
  phone read as the page lagging under the thumb.

  It plays only while it is on screen, stops for good the moment someone taps
  it, has a pause control (WCAG 2.2.2), and holds still under reduced motion.
  Same content as the Loops demo further down (constants.ts): no counts, real
  pool pseudonyms, the archived room read-only.
*/

/** How long the feed shows before the tap, and the tap before the push. */
const FEED_MS = 1500;
const PRESS_MS = 260;
/** Each new message: typing, then the line. */
const TYPING_MS = 750;
const GAP_MS = 850;
/** Reading time on the full room before going back. */
const HOLD_MS = 2600;
/** The pop, before the feed moves to the next room. */
const POP_MS = 650;
/** How many of a room's newest messages arrive while you watch. */
const ARRIVING = 2;

type Screen = "feed" | "chat";

export function PhoneLoop() {
  const { t, i18n } = useTranslation();
  const examples = loopExamplesFor(i18n.language);
  const still = useReducedMotion();
  const device = useRef<HTMLDivElement | null>(null);
  const feed = useRef<HTMLDivElement | null>(null);

  const [index, setIndex] = useState(0);
  const [screen, setScreen] = useState<Screen>("feed");
  const [pressed, setPressed] = useState<number | null>(null);
  const [shown, setShown] = useState(Number.MAX_SAFE_INTEGER);
  const [typing, setTyping] = useState(false);
  /* Someone took over (tapped it) or paused it. Play hands it back. */
  const [held, setHeld] = useState(false);
  const [onScreen, setOnScreen] = useState(false);

  useEffect(() => {
    const el = device.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting), {
      threshold: 0.35,
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const playing = onScreen && !still && !held;

  /*
    One room's run, as a timeline: feed, tap, push, the newest messages arrive
    one by one, a moment to read, pop, next room. Re-entered per room (the last
    step moves `index`) and torn down whenever play stops, so leaving the
    viewport mid-run and coming back starts the room again from the feed.
  */
  useEffect(() => {
    if (!playing) return;
    const loop = examples[index];
    const timers: number[] = [];
    const at = (ms: number, fn: () => void) => timers.push(window.setTimeout(fn, ms));

    setScreen("feed");
    setPressed(null);
    setTyping(false);
    const arriving = loop.archived ? 0 : Math.min(ARRIVING, loop.messages.length - 1);
    setShown(loop.messages.length - arriving);

    let time = FEED_MS;
    at(time, () => setPressed(index));
    time += PRESS_MS;
    at(time, () => {
      setPressed(null);
      setScreen("chat");
    });
    time += 500;
    for (let k = 0; k < arriving; k++) {
      const own = loop.messages[loop.messages.length - arriving + k].own;
      if (!own) {
        at(time, () => setTyping(true));
        time += TYPING_MS;
      }
      at(time, () => {
        setTyping(false);
        setShown((n) => n + 1);
      });
      time += GAP_MS;
    }
    time += HOLD_MS;
    at(time, () => setScreen("feed"));
    time += POP_MS;
    at(time, () => setIndex((i) => (i + 1) % examples.length));

    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [playing, index, examples]);

  /* Keep the room about to be opened in view. Scoped to the feed's own
     scroller, so it never moves the page. */
  useEffect(() => {
    const column = feed.current;
    const card = column?.querySelectorAll<HTMLElement>("[data-card]")[index];
    if (!column || !card) return;
    column.scrollTo({ top: card.offsetTop - column.offsetTop - 10, behavior: still ? "auto" : "smooth" });
  }, [index, still]);

  const open = (i: number) => {
    setHeld(true);
    setTyping(false);
    setPressed(null);
    setShown(Number.MAX_SAFE_INTEGER);
    setIndex(i);
    setScreen("chat");
  };

  const back = () => {
    setHeld(true);
    setScreen("feed");
  };

  const loop = examples[index];
  const ring = useMemo(() => ringPath(), []);

  return (
    <figure className="sh-device" aria-label={t("landing.app.aria")}>
      <svg className="sh-device__ring" viewBox={RING_BOX} aria-hidden>
        <path className="sh-strip--paper" d={ring} />
      </svg>

      <div ref={device} className="sh-phone">
        <div className="sh-phone__screen" data-theme="ink">
          <StatusBar />

          <div className="sh-phone__views">
            {/* ── Feed ── */}
            <div
              className={cn("sh-phone__view sh-phone__view--feed", screen === "chat" && "is-under")}
            >
              <div className="sh-phone__bar">
                <span className="sh-phone__title">{t("rooms.loop.plural")}</span>
                <span className="sh-phone__new" aria-hidden>
                  <Icon glyph={Plus} scale="field" />
                </span>
              </div>
              <div className="sh-phone__chips" aria-hidden>
                <span className="is-on">{t("y.loops.filters.all")}</span>
                <span>{t("y.loops.filters.live")}</span>
                <span>{t("y.loops.filters.loopedIn")}</span>
              </div>
              <div ref={feed} className="sh-phone__feed">
                {examples.map((item, i) => (
                  <PhoneCard
                    key={item.id}
                    loop={item}
                    pressed={pressed === i}
                    onOpen={() => open(i)}
                  />
                ))}
              </div>
              <TabBar />
            </div>

            {/* ── The room ── */}
            <div
              className={cn("sh-phone__view sh-phone__view--chat", screen === "chat" && "is-open")}
            >
              <PhoneRoom loop={loop} shown={shown} typing={typing} onBack={back} />
            </div>
          </div>
          <span className="sh-phone__home" aria-hidden />
        </div>
      </div>

      {!still && (
        <figcaption className="sh-device__caption">
          <button
            type="button"
            className="sh-device__play"
            onClick={() => setHeld((h) => !h)}
          >
            <Icon glyph={held ? Play : Pause} scale="inline" />
            {held ? t("landing.app.play") : t("landing.app.pause")}
          </button>
        </figcaption>
      )}
    </figure>
  );
}

function StatusBar() {
  return (
    <div className="sh-phone__status" aria-hidden>
      <span className="sh-phone__time">9:41</span>
      <span className="sh-phone__island" />
      <span className="sh-phone__signal">
        <Icon glyph={Signal} scale="inline" />
        <Icon glyph={Wifi} scale="inline" />
        <Icon glyph={BatteryFull} scale="field" />
      </span>
    </div>
  );
}

function TabBar() {
  const { t } = useTranslation();
  const tabs = [
    { glyph: MessageSquare, label: t("rooms.string.plural") },
    { glyph: LayoutGrid, label: t("rooms.knot.plural") },
    { glyph: Repeat, label: t("rooms.loop.plural"), on: true },
    { glyph: CircleUser, label: t("landing.app.you") },
  ];
  return (
    <div className="sh-phone__tabs" aria-hidden>
      {tabs.map((tab) => (
        <span key={tab.label} className={cn(tab.on && "is-on")}>
          <Icon glyph={tab.glyph} scale="field" />
          {tab.label}
        </span>
      ))}
    </div>
  );
}

/** A Loop as the app's feed lists it: byline, topic, images, newest line, live or archived. */
function PhoneCard({
  loop,
  pressed,
  onOpen,
}: {
  loop: LoopExample;
  pressed: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const last = loop.messages[loop.messages.length - 1];
  return (
    <button
      type="button"
      data-card
      onClick={onOpen}
      className={cn("sh-pcard", pressed && "is-pressed", loop.archived && "is-archived")}
    >
      <span className="sh-pcard__by">
        <Avatar loop={loop} name={loop.author} />
        <span className="sh-pcard__author">{loop.author}</span>
        {loop.archived && <Icon glyph={RotateCcw} scale="inline" className="sh-pcard__arch" />}
      </span>
      <span className="sh-pcard__topic">{loop.topic}</span>
      {loop.images && <img className="sh-pcard__img" src={loop.images[0]} alt="" />}
      <span className="sh-pcard__last">{last.text}</span>
      <span className="sh-pcard__state">
        {loop.archived ? (
          t("y.loops.archived")
        ) : (
          <>
            <span className="sh-pcard__dot" />
            {t("loops.demo.live")}
          </>
        )}
      </span>
    </button>
  );
}

function PhoneRoom({
  loop,
  shown,
  typing,
  onBack,
}: {
  loop: LoopExample;
  shown: number;
  typing: boolean;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const still = useReducedMotion();
  const lines = loop.messages.slice(0, shown);
  return (
    <>
      <div className="sh-proom__head">
        <button type="button" className="sh-proom__back" onClick={onBack} aria-label={t("landing.app.back")}>
          <Icon glyph={ChevronLeft} scale="control" />
        </button>
        <span className="sh-proom__titles">
          <span className="sh-proom__title">{loop.topic}</span>
          <span className="sh-proom__state">
            {loop.archived ? t("y.loops.archivedTitle") : t("loops.demo.live")}
          </span>
        </span>
      </div>

      {/* Bottom-aligned, newest last; older lines are pushed up and out of
          the top edge rather than the room growing. */}
      <ol className="sh-proom__lines">
        {lines.map((m, i) => {
          const showWho = !m.own && lines[i - 1]?.sender !== m.sender;
          return (
            <motion.li
              key={`${loop.id}-${i}`}
              layout={still ? false : "position"}
              initial={still ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              className={cn("sh-pmsg", m.own && "is-own")}
            >
              {!m.own && (
                <span className={cn("sh-pmsg__avatar", !showWho && "is-gap")}>
                  {showWho && <Avatar loop={loop} name={m.sender} emoji={m.emoji} />}
                </span>
              )}
              <span className="sh-pmsg__col">
                {showWho && <span className="sh-pmsg__who">{m.sender}</span>}
                <span className="sh-pmsg__text">{m.text}</span>
              </span>
            </motion.li>
          );
        })}
        {typing && (
          <motion.li
            key="typing"
            layout={still ? false : "position"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="sh-pmsg sh-pmsg--typing"
            aria-hidden
          >
            <span className="sh-pmsg__avatar is-gap" />
            <span className="sh-pmsg__text">
              <span className="sh-typing" />
              <span className="sh-typing" />
              <span className="sh-typing" />
            </span>
          </motion.li>
        )}
      </ol>

      <div className="sh-proom__foot">
        {loop.archived ? (
          <span className="sh-proom__archived">{t("y.loops.archivedBody")}</span>
        ) : (
          <span className="sh-proom__composer">
            <span>{t("loops.demo.composer")}</span>
            <Icon glyph={SendHorizontal} scale="field" />
          </span>
        )}
      </div>
    </>
  );
}

/** Pseudonym emoji in an anonymous Loop, identity initial in a named one. */
function Avatar({ loop, name, emoji }: { loop: LoopExample; name: string; emoji?: string }) {
  return (
    <span className={cn("sh-pavatar", loop.anonymous ? "is-anon" : getIdentityColor(name))}>
      {loop.anonymous ? emoji ?? <Icon glyph={VenetianMask} scale="inline" /> : name.slice(0, 1)}
    </span>
  );
}

/*
  The paper strip behind the phone: a Loop, joined end to end, the phone
  sitting inside it. The ring runs behind the device, so it never covers the
  screen; what shows is a lobe either side and the glued overlap on the right,
  the same joint the Loop figure further down opens and closes.

  Units: the phone is 400 wide and 866 tall, at x 0..400 in this box.
*/
const RING_BOX = "-260 0 920 866";

function ringPath(): string {
  const cx = 200;
  const cy = 470;
  const rx = 330;
  const ry = 140;
  const tilt = (-14 * Math.PI) / 180;
  const start = -0.35;
  /* A full turn and a bit: the last stretch rides over the start, glued. */
  const sweep = Math.PI * 2 + 0.42;
  const n = 96;
  const pts: Pt[] = [];
  for (let i = 0; i < n; i++) {
    const a = start + (sweep * i) / (n - 1);
    const x = rx * Math.cos(a);
    const y = ry * Math.sin(a);
    pts.push([cx + x * Math.cos(tilt) - y * Math.sin(tilt), cy + x * Math.sin(tilt) + y * Math.cos(tilt)]);
  }
  return ribbon(pts, 44, { start: 0.5, end: 0.5 });
}
